import { z } from 'zod';
import { Tier } from './enums.ts';

/**
 * PUBLIC pricing catalog — the display-only tier ladder the acquisition surface
 * renders (docs/idea/billing.md § "Tiers"). This is the ONE shared source for
 * the public pricing table so marketing, the app, and the API quote the SAME
 * plans; a page never hardcodes a price.
 *
 * NOT the billing authority. `apps/backend/src/billing/tiers.ts` (entitlements,
 * in micros) + `.../billing/rails/ccbill.ts` (the charged price) own what a paid
 * account actually gets. This mirror carries only human-facing display facts —
 * WHOLE credits (never micros), the monthly USD price, and presentation flags —
 * and must be kept in step with that config (docs/idea/billing.md is the spec
 * both trace to). Marketing depends only on `@4f/shared`, so the catalog lives
 * here rather than reaching into the backend.
 */

/** One public plan row — validated display facts for a single {@link Tier}. */
export const pricingTierSchema = z
  .object({
    tier: Tier,
    /** Monthly price in whole USD. `0` on the free tier. */
    priceUsdMonthly: z.number().int().nonnegative(),
    /** Credits granted per month (whole credits, the display unit). `0` on free. */
    monthlyCredits: z.number().int().nonnegative(),
    /** Free-tier daily credit allotment; `null` for paid tiers (balance-bound). */
    dailyCredits: z.number().int().positive().nullable(),
    /** Highlight this row as the recommended plan (exactly one across the ladder). */
    featured: z.boolean(),
  })
  .strict()
  // Cross-field invariant: the two tier models are mutually exclusive. `free` is a
  // daily-allotment tier (no price, no monthly grant); every paid tier is
  // balance-bound (a price, a monthly grant, and no daily cap). Rejects the
  // impossible mixes the per-field rules alone would let through.
  .refine(
    ({ tier, priceUsdMonthly, monthlyCredits, dailyCredits }) =>
      tier === 'free'
        ? priceUsdMonthly === 0 && monthlyCredits === 0 && dailyCredits !== null
        : priceUsdMonthly > 0 && monthlyCredits > 0 && dailyCredits === null,
    { message: 'Tier pricing fields do not match the tier model' },
  );

/** A validated public plan row. */
export type PricingTier = z.infer<typeof pricingTierSchema>;

/**
 * The catalog keyed by tier. Declared as `satisfies Record<Tier, …>` so it is
 * EXHAUSTIVE at compile time: add a value to the `Tier` enum and this object
 * stops typechecking until its row exists (the "add a plan in one place" rule).
 */
const CATALOG = {
  free: { tier: 'free', priceUsdMonthly: 0, monthlyCredits: 0, dailyCredits: 50, featured: false },
  starter: {
    tier: 'starter',
    priceUsdMonthly: 3,
    monthlyCredits: 400,
    dailyCredits: null,
    featured: false,
  },
  plus: {
    tier: 'plus',
    priceUsdMonthly: 12,
    monthlyCredits: 2_000,
    dailyCredits: null,
    featured: true,
  },
  pro: {
    tier: 'pro',
    priceUsdMonthly: 40,
    monthlyCredits: 8_000,
    dailyCredits: null,
    featured: false,
  },
  max: {
    tier: 'max',
    priceUsdMonthly: 200,
    monthlyCredits: 50_000,
    dailyCredits: null,
    featured: false,
  },
} satisfies Record<Tier, PricingTier>;

/** Presentation order — cheapest first (the order the pricing table renders). */
export const PRICING_ORDER: readonly Tier[] = ['free', 'starter', 'plus', 'pro', 'max'];

/** The full public ladder, ordered by {@link PRICING_ORDER}. */
export const PRICING_TIERS: readonly PricingTier[] = PRICING_ORDER.map((tier) => CATALOG[tier]);

/**
 * The public plan row for a tier. Total over {@link Tier} (the catalog is
 * exhaustive), so it never throws — a validated `Tier` always resolves.
 */
export function pricingTier(tier: Tier): PricingTier {
  return CATALOG[tier];
}
